import { DetailPageDemoComponent } from 'src/app/modules/standalone/detail-page-demo/detail-page-demo.component'
import { ApiKeyRevealComponent } from 'src/app/modules/standalone/api-key-reveal/api-key-reveal.component'
import { LoginComponent } from 'src/app/modules/standalone/login/login.component'
import { SignupComponent } from 'src/app/modules/standalone/signup/signup.component'
import { OrganizationAccessComponent } from './modules/standalone/organization-access/organization-access.component'
import { UpgradeTierComponent } from '@billing/upgrade-tier/upgrade-tier.component'
import { capabilitiesReadyGuard, legacyStripeGuard } from '@services/runtime-capabilities.service'
import { environment } from 'src/environments/environment'

import { NgModule } from '@angular/core'
import { RouterModule, Routes } from '@angular/router'
import {
  canAccessBillingGuard, canEditProjectGuard, canViewProjectGuard, CustomAuthGuard, hasSubscriptionGuard, isNotLegacySubscription,
  isProTierGuard, isSuperAdminGuard, isUserAdminGuard, moduleVisibilityGuard, oeeShellRoleGuard, oeeSubPageGuard, roleModuleGuard,
  projectIsValidGuard, subscriptionIsActiveGuard, signupGuard, hasNoOrganizationGuard, hasNoSubscriptionGuard, isFreeTierGuard
} from '@classes/RouteGuards'
import { ErrorPageComponent } from '@shared/error-page/error-page.component'

import { SubmitFeedbackComponent } from './modules/standalone/submit-feedback/submit-feedback.component'

export const appRoutes: Routes = [
    { path: 'organization-access', component: OrganizationAccessComponent },
    {
        path: 'assets',
        loadChildren: () => import('@assets/asset-management.module').then(m => m.AssetManagementModule),
        canActivate: [CustomAuthGuard, hasSubscriptionGuard, subscriptionIsActiveGuard, isProTierGuard, moduleVisibilityGuard('assets'), roleModuleGuard('assets')]
    },
    {
        path: 'billing',
        loadChildren: () => import('@billing/billing.module').then(m => m.BillingModule),
        canActivate: [CustomAuthGuard, canAccessBillingGuard, isNotLegacySubscription, moduleVisibilityGuard('billing'), roleModuleGuard('billing')]
    },
    {
        path: 'projects/edit/:id',
        loadChildren: () => import('@builder/builder.module').then(m => m.BuilderModule),
        canActivate: [projectIsValidGuard, CustomAuthGuard, hasSubscriptionGuard, subscriptionIsActiveGuard, canEditProjectGuard, moduleVisibilityGuard('projects'), roleModuleGuard('projects_edit')]
    },
    {
        path: 'builder/:id',
        redirectTo: 'projects/edit/:id',
    },
    {
        path: 'projects',
        loadChildren: () => import('@dashboard/dashboard.module').then(m => m.DashboardModule),
        canActivate: [CustomAuthGuard, hasSubscriptionGuard, subscriptionIsActiveGuard, moduleVisibilityGuard('projects'), roleModuleGuard('projects')]
    },
    {
        path: 'dashboard',
        redirectTo: 'projects',
    },
    {
        path: 'login',
        component: LoginComponent,
    },
    {
        path: 'api-key/reveal',
        component: ApiKeyRevealComponent,
        canActivate: [CustomAuthGuard]
    },
    {
        path: 'models',
        loadChildren: () => import('@models/model-manager.module').then(m => m.ModelManagerModule),
        canActivate: [CustomAuthGuard, hasSubscriptionGuard, subscriptionIsActiveGuard, moduleVisibilityGuard('models'), roleModuleGuard('models')]
    },
    {
        path: 'model-manager',
        redirectTo: 'models',
    },
    {
        path: 'files',
        loadChildren: () => import('./modules/files/files.module').then(m => m.FilesModule),
        canActivate: [CustomAuthGuard, hasSubscriptionGuard, subscriptionIsActiveGuard, moduleVisibilityGuard('files'), roleModuleGuard('files')]
    },
    {
        path: 'my-shifts',
        loadChildren: () => import('./modules/my-shifts/my-shifts.module').then(m => m.MyShiftsModule),
        canActivate: [CustomAuthGuard, hasSubscriptionGuard, subscriptionIsActiveGuard, moduleVisibilityGuard('my-shifts'), roleModuleGuard('my-shifts')]
    },
    {
        path: 'oee-dashboard',
        redirectTo: 'oee',
    },
    {
        path: 'oee',
        loadChildren: () => import('./features/oee-dashboard/oee-dashboard.module').then(m => m.OEEDashboardModule),
        canActivate: [CustomAuthGuard, hasSubscriptionGuard, subscriptionIsActiveGuard, moduleVisibilityGuard('oee'), oeeShellRoleGuard]
    },
    {
        path: 'process-flow',
        loadChildren: () => import('./features/schematic-builder/schematic-builder.module').then(m => m.SchematicBuilderModule),
        canActivate: [CustomAuthGuard, hasSubscriptionGuard, subscriptionIsActiveGuard, moduleVisibilityGuard('oee'), oeeSubPageGuard('enable_process_flow'), roleModuleGuard('oee_process_flow')]
    },
    {
        path: 'organization',
        loadChildren: () => import('@organization/organization.module').then(m => m.OrganizationModule),
        canActivate: [CustomAuthGuard, hasSubscriptionGuard, isUserAdminGuard, moduleVisibilityGuard('organization'), roleModuleGuard('organization')]
    },
    {
        path: 'admin/org-settings',
        loadChildren: () => import('./modules/admin/admin.module').then(m => m.AdminModule),
        canActivate: [CustomAuthGuard, isSuperAdminGuard]
    },
    {
        path: 'signup',
        canActivate: [legacyStripeGuard],
        children: [{ path: '', component: SignupComponent, canActivate: [signupGuard, hasNoOrganizationGuard, hasNoSubscriptionGuard] }],
    },
    {
        path: 'upgrade',
        canActivate: [legacyStripeGuard],
        children: [{ path: '', component: UpgradeTierComponent, canActivate: [CustomAuthGuard, hasSubscriptionGuard, isFreeTierGuard] }],
    },
    {
        path: 'feedback',
        component: SubmitFeedbackComponent,
        canActivate: [CustomAuthGuard, hasSubscriptionGuard]
    },
    {
        path: 'projects/:id',
        loadChildren: () => import('@viewer/viewer.module').then(m => m.ViewerModule),
        canActivate: [projectIsValidGuard, canViewProjectGuard]
    },
    {
        path: 'viewer/:id',
        redirectTo: 'projects/:id',
    },
    {
        path: 'error/:code',
        component: ErrorPageComponent,
    },
    // Living Style Guide — dev-only. Filtered out of the routing table when
    // building for production so the route literally does not exist there.
    ...(environment.production ? [] : [
        { path: 'design-system', redirectTo: 'dev/style-guide', pathMatch: 'full' as const },
        { path: 'dev/style-guide/details', loadComponent: () => import('src/app/modules/standalone/living-style-guide/living-style-guide.component').then(m => m.LivingStyleGuideComponent) },
        {
            path: 'dev/style-guide',
            loadComponent: () => import('src/app/modules/standalone/living-style-guide/showcase.component').then(m => m.ShowcaseComponent),
        },
        {
            path: 'dev/style-guide/tables',
            loadComponent: () => import('src/app/modules/standalone/living-style-guide/living-style-guide.component').then(m => m.LivingStyleGuideComponent),
        }
    ]),
    {
        path: 'dev/detail-page-demo',
        component: DetailPageDemoComponent,
    },
    {
        path: '',
        redirectTo: '/projects',
        pathMatch: 'full'
    },
    {
        path: '**',
        redirectTo: 'error/404'
    }
];

@NgModule({
    imports: [RouterModule.forRoot([
        { path: '', canActivateChild: [capabilitiesReadyGuard], children: appRoutes },
    ], { initialNavigation: 'disabled' })],
    exports: [RouterModule],
    providers: []
})
export class AppRoutingModule { }
